import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, MessageCircle, Shield, Sparkles, ArrowLeft } from 'lucide-react';
import heroImage from "@/assets/hero-bg.jpg";
import romanticAvatar from "@/assets/realistic-romantic.jpg";
import intellectualAvatar from "@/assets/realistic-intellectual.jpg";
import sportyAvatar from "@/assets/realistic-sporty.jpg";
import protectorAvatar from "@/assets/realistic-protector.jpg";
import { useTranslation } from '@/hooks/useTranslation';

interface BenefitsPageProps {
  onNext: () => void;
  onBack?: () => void;
}

export const BenefitsPage: React.FC<BenefitsPageProps> = ({ onNext, onBack }) => {
  const { t } = useTranslation();
  const boyfriends = [
    { 
      image: romanticAvatar, 
      alt: t('benefits.bf_romantic_alt'),
      name: "Marcus",
      age: 28,
      occupation: t('benefits.occupation_photographer')
    },
    { 
      image: intellectualAvatar, 
      alt: t('benefits.bf_intellectual_alt'),
      name: "Daniel",
      age: 32,
      occupation: t('benefits.occupation_professor')
    },
    { 
      image: sportyAvatar, 
      alt: t('benefits.bf_sporty_alt'),
      name: "Jake",
      age: 26,
      occupation: t('benefits.occupation_trainer')
    },
    { 
      image: protectorAvatar, 
      alt: t('benefits.bf_protector_alt'),
      name: "Ryan",
      age: 30,
      occupation: t('benefits.occupation_firefighter')
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % boyfriends.length);
    }, 1500); // Change photo every 1.5 seconds

    return () => clearInterval(interval);
  }, [boyfriends.length]);
  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Back Button */}
      {onBack && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="fixed left-4 z-50 bg-background/80 backdrop-blur-sm hover:bg-background/90"
          style={{ top: `calc(1rem + var(--safe-area-inset-top, 0px))` }}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      )}
      
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-dreamy" />
      
      {/* Top 2/3 Boyfriend Showcase Background */}
      <div className="absolute inset-0 h-2/3">
        {boyfriends.map((boyfriend, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img 
              src={boyfriend.image} 
              alt={boyfriend.alt} 
              className="w-full h-full object-cover"
            />
            {/* Overlay for better text readability */}
            <div className="absolute inset-0 bg-black/30" />
            
            {/* Name, Age, Occupation Overlay */}
            <div className="absolute bottom-8 left-0 right-0 p-6">
              <div className="text-white text-left max-w-4xl mx-auto px-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <h3 className="text-3xl md:text-4xl font-bold">{boyfriend.name}, {boyfriend.age}</h3>
                </div>
                <p className="text-lg md:text-xl text-primary-glow font-medium">{boyfriend.occupation}</p>
              </div>
            </div>
          </div>
        ))}
        {/* Fade transition at bottom of photos */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none"></div>
      </div>
      
      {/* Floating Content in Bottom Third */}
      <div className="relative z-20 min-h-screen flex flex-col justify-end pb-16 px-4">
        <div className="animate-fade-in space-y-8">
          {/* Header */}
          <div className="animate-fade-in opacity-0 text-center" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
              {t('benefits.title')}
            </h1>
          </div>
          
          {/* Call to Action */}
          <div className="space-y-4 text-center">
            <Button 
              variant="dreamy" 
              size="xl" 
              onClick={onNext}
              className="animate-scale-in opacity-0 shadow-2xl"
              style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}
            >
              <Heart className="h-5 w-5 mr-2" />
              {t('common.next')}
            </Button>
            
            <p className="text-sm text-muted-foreground animate-fade-in opacity-0" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
              {t('common.by_clicking_next')}{" "}
              <a href="#" className="text-primary hover:underline">
                {t('common.terms_and_conditions')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};